import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import s from './MoveInventoryItemQuantityModal.module.css';
import sg from '../../../styles/index.module.css';
import { Overlay } from '../Overlay/Overlay';
import { cancelMoveInventory } from '../../store/slices/moveInventoryQuantityModalSlice';
import {
    boardSlice,
    sendMoveInventoryRequest,
    sendMoveInventoryToWarehouseFullyRequest,
    sendMoveInventoryToWarehousePartiallyRequest,
    updateInventoryAmount,
} from '../../store/slices/boardSlice';
import { useTranslation } from 'react-i18next';

export function MoveInventoryItemQuantityModal() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [value, setValue] = useState(null);

    const moveInventoryQuantityModal = useSelector((state) => state.moveInventoryQuantityModal);

    const isOpen = moveInventoryQuantityModal.isModalOpened;

    if (
        !isOpen ||
        !moveInventoryQuantityModal.warehouseFrom ||
        !moveInventoryQuantityModal.warehouseTo
    ) {
        return null;
    }

    const { amount } = moveInventoryQuantityModal.inventory;
    if (value === null) {
        setValue(amount);
    }

    const floatValue = numberFormatter.parseNumber(value);

    const cancelMove = () => {
        dispatch(
            boardSlice.actions.moveInventoryToWarehouse({
                inventoryPosition: moveInventoryQuantityModal.inventory.sort_index,
                inventory: moveInventoryQuantityModal.inventory,
                warehouseId: moveInventoryQuantityModal.warehouseFrom.id,
            })
        );
        dispatch(
            boardSlice.actions.removeInventoryFromWarehouse({
                inventoryPosition: moveInventoryQuantityModal.newInventoryPosition,
                warehouseId: moveInventoryQuantityModal.warehouseTo.id,
            })
        );
        dispatch(cancelMoveInventory(false));
        setValue(null);
    };

    const doMove = () => {
        if (parseFloat(amount) === floatValue) {
            if (moveInventoryQuantityModal.isDragAndDropMovingMode) {
                dispatch(
                    boardSlice.actions.removeInventoryFromWarehouse({
                        inventoryPosition: moveInventoryQuantityModal.newInventoryPosition,
                        warehouseId: moveInventoryQuantityModal.warehouseTo.id,
                    })
                );

                dispatch(
                    sendMoveInventoryToWarehouseFullyRequest({
                        inventory: moveInventoryQuantityModal.inventory,
                        warehouseId: moveInventoryQuantityModal.warehouseTo.id,
                        inventoryPosition: moveInventoryQuantityModal.newInventoryPosition,
                        fromWarehouseId: moveInventoryQuantityModal.warehouseFrom.id,
                    })
                );
            } else {
                dispatch(
                    sendMoveInventoryRequest({
                        inventoryGuid: moveInventoryQuantityModal.inventory.guid,
                        fromWarehouseId: moveInventoryQuantityModal.warehouseFrom.id,
                        toWarehouseId: moveInventoryQuantityModal.warehouseTo.id,
                        newIndex: moveInventoryQuantityModal.newInventoryPosition,
                        inventory: moveInventoryQuantityModal.inventory,
                    })
                );
            }
        } else {
            dispatch(
                sendMoveInventoryToWarehousePartiallyRequest({
                    warehouseToId: moveInventoryQuantityModal.warehouseTo.id,
                    warehouseFromId: moveInventoryQuantityModal.warehouseFrom.id,
                    inventoryGuid: moveInventoryQuantityModal.inventory.guid,
                    inventoryPosition: moveInventoryQuantityModal.newInventoryPosition,
                    quantity: floatValue,
                })
            );

            if (moveInventoryQuantityModal.isDragAndDropMovingMode) {
                dispatch(
                    boardSlice.actions.moveInventoryToWarehouse({
                        inventoryPosition: moveInventoryQuantityModal.inventory.sort_index,
                        inventory: moveInventoryQuantityModal.inventory,
                        warehouseId: moveInventoryQuantityModal.warehouseFrom.id,
                    })
                );
            }
            dispatch(
                updateInventoryAmount({
                    warehouseId: moveInventoryQuantityModal.warehouseFrom.id,
                    inventoryGuid: moveInventoryQuantityModal.inventory.guid,
                    amount: amount - floatValue,
                })
            );
            if (moveInventoryQuantityModal.isDragAndDropMovingMode) {
                dispatch(
                    updateInventoryAmount({
                        warehouseId: moveInventoryQuantityModal.warehouseTo.id,
                        inventoryGuid: moveInventoryQuantityModal.inventory.guid,
                        amount: floatValue,
                    })
                );
            }
        }

        dispatch(cancelMoveInventory(false));
        setValue(null);
    };

    const isValidValue = floatValue > 0 && floatValue <= amount;

    return (
        <Overlay>
            <div>
                <div className={s.modal}>
                    <div className={s.modalBody}>
                        <span className={s.modalTitle}>
                            {t('MoveInventoryItemQuantityModal.modalTitle.text')}
                        </span>
                        <div className={s.form}>
                            <input
                                type="text"
                                className={cx(sg.input, s.input, !isValidValue ? s.errorInput : '')}
                                value={value}
                                ref={(inputElement) => {
                                    if (inputElement) {
                                        setTimeout(() => {
                                            inputElement.focus();
                                        }, 100);
                                    }
                                }}
                                onFocus={(e) => e.currentTarget.select()}
                                onBlur={(e) => {
                                    const { value: newValue } = e.target;
                                    const regexp = /^[0-9\-,.\s)(]*$/;
                                    if (regexp.test(newValue) && newValue !== '') {
                                        setValue(newValue);
                                    } else if (newValue === '') {
                                        setValue('');
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.currentTarget.blur();
                                    }
                                }}
                                onChange={(e) => {
                                    const { value: newValue } = e.target;
                                    const regexp = /^[0-9\-,.\s)(]*$/;
                                    if (regexp.test(newValue) && newValue !== '') {
                                        setValue(newValue);
                                    } else if (newValue === '') {
                                        setValue('');
                                    }
                                }}
                            />
                            <span className={s.fromText}>
                                {t('MoveInventoryItemQuantityModal.from.text')}
                            </span>
                            <span className={s.fromAmount}>
                                {numberFormatter.formatNumber(amount, 3)}
                            </span>
                        </div>
                        <div className={s.errorMessage}>
                            {floatValue <= 0 && (
                                <span>
                                    {t('MoveInventoryItemQuantityModal.floatLess0Error.text')}
                                </span>
                            )}
                            {floatValue > amount && (
                                <span>{t('MoveInventoryItemQuantityModal.amountError.text')}</span>
                            )}
                        </div>
                        <button
                            className={cx(s.moveButton, 'btn btn-primary')}
                            onClick={() => {
                                doMove();
                            }}
                            type="submit"
                            disabled={!isValidValue}
                        >
                            {t('MoveInventoryItemQuantityModal.MoveBtn.text')}
                        </button>
                    </div>
                    <div
                        className={s.closeBtn}
                        onClick={() => {
                            cancelMove();
                        }}
                    />
                </div>
            </div>
        </Overlay>
    );
}
